import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../ui/alert-dialog';
import {
  Briefcase,
  Plus,
  RefreshCw,
  Trash2,
  Edit,
  Eye,
  EyeOff,
  Loader2,
  Save,
  X,
  Sparkles,
  Users,
  FileText,
  Linkedin,
  Link as LinkIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  jobsApi,
  POST_TYPE_LABELS,
  EMPLOYMENT_LABELS,
} from '../../../pages/pharmacy/careersApi';
import ApplicationsTab from './ApplicationsTab';
import { shareToSocial } from '../../../lib/socialShare';

const EMPTY_JOB = {
  slug: '',
  title: '',
  post_type: 'auxiliaire',
  employment_type: 'CDI',
  status: 'draft',
  location: 'Cocody Angré 7e Tranche, Abidjan',
  city: 'Abidjan',
  country: 'CI',
  country_name: "Côte d'Ivoire",
  description_short: '',
  missions: [],
  profile: [],
  offer: [],
  experience_years: 0,
  education_level: '',
  deadline_iso: '',
  display_order: 0,
  salary_currency: 'XOF',
  salary_min: null,
  salary_max: null,
  salary_period: 'MONTH',
};

const linesToList = (s) =>
  (s || '')
    .split('\n')
    .map((x) => x.trim())
    .filter(Boolean);

const listToLines = (arr) => (arr || []).join('\n');

const JobEditor = ({ job, onCancel, onSaved }) => {
  const isNew = !job?.id;
  const [form, setForm] = useState({
    ...EMPTY_JOB,
    ...(job || {}),
    _missionsText: listToLines(job?.missions || []),
    _profileText: listToLines(job?.profile || []),
    _offerText: listToLines(job?.offer || []),
  });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || form.title.trim().length < 3) {
      toast.error('Le titre est trop court.');
      return;
    }
    if (!form.description_short || form.description_short.trim().length < 20) {
      toast.error('La description courte doit faire au moins 20 caractères.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        slug: form.slug || undefined,
        title: form.title.trim(),
        post_type: form.post_type,
        employment_type: form.employment_type,
        status: form.status,
        location: form.location,
        city: form.city,
        country: form.country,
        country_name: form.country_name,
        description_short: form.description_short.trim(),
        missions: linesToList(form._missionsText),
        profile: linesToList(form._profileText),
        offer: linesToList(form._offerText),
        experience_years: parseInt(form.experience_years, 10) || 0,
        education_level: form.education_level || null,
        deadline_iso: form.deadline_iso || null,
        display_order: parseInt(form.display_order, 10) || 0,
        salary_currency: form.salary_currency,
        salary_min: form.salary_min ? parseInt(form.salary_min, 10) : null,
        salary_max: form.salary_max ? parseInt(form.salary_max, 10) : null,
        salary_period: form.salary_period,
      };
      const saved = isNew ? await jobsApi.create(payload) : await jobsApi.update(job.id, payload);
      toast.success(isNew ? 'Fiche créée' : 'Fiche mise à jour');
      onSaved(saved);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="job-editor">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block md:col-span-2">
          <span className="text-sm font-medium text-slate-700">Titre du poste *</span>
          <input
            type="text"
            required
            value={form.title}
            onChange={set('title')}
            className="mt-1 w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#5D4E37]"
            data-testid="job-editor-title"
          />
        </label>

        <label className="block">
          <span className="text-sm font-medium text-slate-700">Slug (URL)</span>
          <input
            type="text"
            value={form.slug}
            onChange={set('slug')}
            placeholder="auto-généré depuis le titre"
            className="mt-1 w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#5D4E37]"
            data-testid="job-editor-slug"
          />
        </label>

        <label className="block">
          <span className="text-sm font-medium text-slate-700">Statut *</span>
          <select
            value={form.status}
            onChange={set('status')}
            className="mt-1 w-full border rounded-md px-3 py-2"
            data-testid="job-editor-status"
          >
            <option value="draft">Brouillon</option>
            <option value="published">Publié</option>
          </select>
        </label>

        <label className="block">
          <span className="text-sm font-medium text-slate-700">Type de poste *</span>
          <select
            value={form.post_type}
            onChange={set('post_type')}
            className="mt-1 w-full border rounded-md px-3 py-2"
            data-testid="job-editor-post-type"
          >
            {Object.entries(POST_TYPE_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="text-sm font-medium text-slate-700">Contrat *</span>
          <select
            value={form.employment_type}
            onChange={set('employment_type')}
            className="mt-1 w-full border rounded-md px-3 py-2"
            data-testid="job-editor-employment"
          >
            {Object.entries(EMPLOYMENT_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </label>

        <label className="block md:col-span-2">
          <span className="text-sm font-medium text-slate-700">Description courte *</span>
          <textarea
            required
            rows={3}
            value={form.description_short}
            onChange={set('description_short')}
            className="mt-1 w-full border rounded-md px-3 py-2 resize-y"
            data-testid="job-editor-description"
          />
        </label>

        <label className="block md:col-span-2">
          <span className="text-sm font-medium text-slate-700">Missions (1 par ligne)</span>
          <textarea
            rows={6}
            value={form._missionsText}
            onChange={set('_missionsText')}
            className="mt-1 w-full border rounded-md px-3 py-2 resize-y font-mono text-sm"
            data-testid="job-editor-missions"
          />
        </label>

        <label className="block md:col-span-2">
          <span className="text-sm font-medium text-slate-700">Profil recherché (1 par ligne)</span>
          <textarea
            rows={5}
            value={form._profileText}
            onChange={set('_profileText')}
            className="mt-1 w-full border rounded-md px-3 py-2 resize-y font-mono text-sm"
            data-testid="job-editor-profile"
          />
        </label>

        <label className="block md:col-span-2">
          <span className="text-sm font-medium text-slate-700">Ce que SADA offre (1 par ligne)</span>
          <textarea
            rows={5}
            value={form._offerText}
            onChange={set('_offerText')}
            className="mt-1 w-full border rounded-md px-3 py-2 resize-y font-mono text-sm"
            data-testid="job-editor-offer"
          />
        </label>

        <label className="block">
          <span className="text-sm font-medium text-slate-700">Années d'expérience</span>
          <input
            type="number"
            min="0"
            value={form.experience_years}
            onChange={set('experience_years')}
            className="mt-1 w-full border rounded-md px-3 py-2"
          />
        </label>

        <label className="block">
          <span className="text-sm font-medium text-slate-700">Niveau d'études</span>
          <input
            type="text"
            value={form.education_level || ''}
            onChange={set('education_level')}
            placeholder="Ex : BT/BTS Pharmacie"
            className="mt-1 w-full border rounded-md px-3 py-2"
          />
        </label>

        <label className="block">
          <span className="text-sm font-medium text-slate-700">Ville</span>
          <input
            type="text"
            value={form.city}
            onChange={set('city')}
            className="mt-1 w-full border rounded-md px-3 py-2"
          />
        </label>

        <label className="block">
          <span className="text-sm font-medium text-slate-700">Date limite (ISO)</span>
          <input
            type="date"
            value={(form.deadline_iso || '').slice(0, 10)}
            onChange={set('deadline_iso')}
            className="mt-1 w-full border rounded-md px-3 py-2"
          />
        </label>

        <label className="block">
          <span className="text-sm font-medium text-slate-700">Salaire min (XOF)</span>
          <input
            type="number"
            min="0"
            value={form.salary_min || ''}
            onChange={set('salary_min')}
            className="mt-1 w-full border rounded-md px-3 py-2"
          />
        </label>

        <label className="block">
          <span className="text-sm font-medium text-slate-700">Salaire max (XOF)</span>
          <input
            type="number"
            min="0"
            value={form.salary_max || ''}
            onChange={set('salary_max')}
            className="mt-1 w-full border rounded-md px-3 py-2"
          />
        </label>

        <label className="block">
          <span className="text-sm font-medium text-slate-700">Ordre d'affichage</span>
          <input
            type="number"
            value={form.display_order}
            onChange={set('display_order')}
            className="mt-1 w-full border rounded-md px-3 py-2"
          />
        </label>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel} data-testid="job-editor-cancel">
          <X className="w-4 h-4 mr-2" /> Annuler
        </Button>
        <Button
          type="submit"
          disabled={saving}
          className="bg-[#5D4E37] hover:bg-[#4A3E2C] text-white"
          data-testid="job-editor-save"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Enregistrer
        </Button>
      </div>
    </form>
  );
};

const JobPostingsTab = () => {
  const [subTab, setSubTab] = useState('jobs');
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {} for new, {..job} for edit
  const [deleteId, setDeleteId] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await jobsApi.listAdmin();
      setJobs(res.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur chargement fiches de poste');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const res = await jobsApi.seedDefaults();
      toast.success(`✅ ${res.created} créées, ${res.updated} mises à jour`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur seed');
    } finally {
      setSeeding(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await jobsApi.remove(deleteId);
      toast.success('Fiche supprimée');
      setDeleteId(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur suppression');
    }
  };

  const togglePublish = async (job) => {
    try {
      const next = job.status === 'published' ? 'draft' : 'published';
      await jobsApi.update(job.id, { status: next });
      toast.success(next === 'published' ? 'Fiche publiée' : 'Fiche dépubliée');
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  if (editing !== null) {
    return (
      <div className="space-y-4" data-testid="job-postings-editor">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-[#5D4E37] flex items-center gap-2">
            <Briefcase className="w-5 h-5" />
            {editing.id ? 'Modifier la fiche de poste' : 'Nouvelle fiche de poste'}
          </h3>
        </div>
        <Card>
          <CardContent className="p-6">
            <JobEditor
              job={editing.id ? editing : null}
              onCancel={() => setEditing(null)}
              onSaved={() => {
                setEditing(null);
                refresh();
              }}
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="job-postings-tab">
      {/* Sub-tabs Fiches / Candidatures */}
      <div className="flex gap-2 border-b border-[#E8DDD4] pb-0">
        <button
          onClick={() => setSubTab('jobs')}
          className={`px-4 py-2 font-medium text-sm rounded-t-lg transition-colors ${
            subTab === 'jobs'
              ? 'bg-[#5D4E37] text-white'
              : 'text-[#78716C] hover:bg-slate-100'
          }`}
          data-testid="subtab-jobs"
        >
          <FileText className="w-4 h-4 inline mr-2" />
          Fiches de poste (TDR)
        </button>
        <button
          onClick={() => setSubTab('applications')}
          className={`px-4 py-2 font-medium text-sm rounded-t-lg transition-colors ${
            subTab === 'applications'
              ? 'bg-[#5D4E37] text-white'
              : 'text-[#78716C] hover:bg-slate-100'
          }`}
          data-testid="subtab-applications"
        >
          <Users className="w-4 h-4 inline mr-2" />
          Candidatures reçues
        </button>
      </div>

      {subTab === 'applications' ? (
        <ApplicationsTab />
      ) : (
        <>
          <Card>
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-[#5D4E37]">
            <Briefcase className="w-5 h-5" />
            <span className="font-semibold">Fiches de poste (TDR)</span>
            <Badge variant="outline">{jobs.length}</Badge>
          </div>
          <div className="flex-1" />
          <Button
            size="sm"
            variant="outline"
            onClick={refresh}
            disabled={loading}
            data-testid="jobs-refresh"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Actualiser
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleSeed}
            disabled={seeding}
            data-testid="jobs-seed"
          >
            {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            Importer les 3 TDR par défaut
          </Button>
          <Button
            size="sm"
            className="bg-[#5D4E37] hover:bg-[#4A3E2C] text-white"
            onClick={() => setEditing({})}
            data-testid="jobs-new"
          >
            <Plus className="w-4 h-4" /> Nouvelle fiche
          </Button>
        </CardContent>
      </Card>

      {loading && jobs.length === 0 ? (
        <div className="flex items-center justify-center py-16 text-slate-500">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Chargement…
        </div>
      ) : jobs.length === 0 ? (
        <Card>
          <CardContent className="text-center py-14 text-slate-500">
            Aucune fiche de poste. Cliquez sur <strong>« Importer les 3 TDR par défaut »</strong>
            {' '}pour démarrer, ou créez-en une nouvelle.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {jobs.map((j) => (
            <Card key={j.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4 flex flex-wrap items-start gap-3">
                <div className="flex-1 min-w-[240px]">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h4 className="font-semibold text-[#5D4E37]">{j.title}</h4>
                    <Badge
                      className={
                        j.status === 'published'
                          ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-100'
                      }
                    >
                      {j.status === 'published' ? 'Publié' : 'Brouillon'}
                    </Badge>
                    <Badge variant="outline">{EMPLOYMENT_LABELS[j.employment_type] || j.employment_type}</Badge>
                    <Badge variant="outline">{POST_TYPE_LABELS[j.post_type] || j.post_type}</Badge>
                  </div>
                  <div className="text-xs text-slate-500 mb-1 font-mono">/{`recrutement/${j.slug}`}</div>
                  <p className="text-sm text-slate-600 line-clamp-2">{j.description_short}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(() => {
                    const origin =
                      typeof window !== 'undefined'
                        ? window.location.origin
                        : 'https://phciesada.org';
                    const shareUrl = `${origin}/recrutement/${j.slug}`;
                    const ogUrl = `${origin}/api/careers/jobs/${j.slug}/og?v=${Date.now()}`;
                    const disabled = j.status !== 'published';
                    const doShare = (ch) =>
                      shareToSocial(ch, {
                        url: shareUrl,
                        ogUrl,
                        title: j.title,
                        subject: `${j.title} — Pharmacie SADA recrute`,
                      });
                    const hint = disabled
                      ? 'Publiez d\u2019abord la fiche pour la partager'
                      : undefined;
                    return (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-green-600 border-green-200"
                          disabled={disabled}
                          onClick={() => doShare('whatsapp')}
                          title={hint || 'Partager sur WhatsApp'}
                          data-testid={`jobs-share-whatsapp-${j.slug}`}
                        >
                          WhatsApp
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-blue-600 border-blue-200"
                          disabled={disabled}
                          onClick={() => doShare('facebook')}
                          title={hint || 'Partager sur Facebook'}
                          data-testid={`jobs-share-facebook-${j.slug}`}
                        >
                          Facebook
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-pink-600 border-pink-200 hover:bg-pink-50"
                          disabled={disabled}
                          onClick={() => doShare('instagram')}
                          title={hint || 'Partager sur Instagram'}
                          data-testid={`jobs-share-instagram-${j.slug}`}
                        >
                          Instagram
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-neutral-900 border-neutral-300 hover:bg-neutral-100"
                          disabled={disabled}
                          onClick={() => doShare('tiktok')}
                          title={hint || 'Partager sur TikTok'}
                          data-testid={`jobs-share-tiktok-${j.slug}`}
                        >
                          TikTok
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-[#0A66C2] border-[#0A66C2]/40 hover:bg-[#0A66C2]/10"
                          disabled={disabled}
                          onClick={() => doShare('linkedin')}
                          title={hint || 'Partager sur LinkedIn'}
                          data-testid={`jobs-share-linkedin-${j.slug}`}
                        >
                          <Linkedin className="w-4 h-4" />
                          LinkedIn
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => doShare('copy')}
                          title="Copier le lien public"
                          data-testid={`jobs-copy-link-${j.slug}`}
                        >
                          <LinkIcon className="w-4 h-4" />
                          Copier
                        </Button>
                      </>
                    );
                  })()}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => togglePublish(j)}
                    data-testid={`jobs-toggle-${j.slug}`}
                  >
                    {j.status === 'published' ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    {j.status === 'published' ? 'Dépublier' : 'Publier'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditing(j)}
                    data-testid={`jobs-edit-${j.slug}`}
                  >
                    <Edit className="w-4 h-4" /> Modifier
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-red-300 text-red-700 hover:bg-red-50"
                    onClick={() => setDeleteId(j.id)}
                    data-testid={`jobs-delete-${j.slug}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette fiche de poste ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est définitive. Les candidatures déjà reçues restent en base.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="jobs-delete-confirm"
            >
              Supprimer définitivement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
        </>
      )}
    </div>
  );
};

export default JobPostingsTab;
